import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Button, Stack } from '@mui/material';
import { isNewPlanId } from '@provision/service-level-utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { DynamicIcon } from 'src/components';
import { useGroupRules, UserGroupRules, useServiceLevels } from 'src/graphql';
import { ArchivedPlanItem } from 'src/pages/Settings/memberships/components/plans-tab/archived-plan-item';
import { ArchivedPlansSection } from 'src/pages/Settings/memberships/components/plans-tab/archived-plans-section';
import { BillingOptions } from 'src/pages/Settings/memberships/components/plans-tab/billing-options';
import { CtaSupportSection } from 'src/pages/Settings/memberships/components/plans-tab/cta-support-section';
import { MemoFaqItem } from 'src/pages/Settings/memberships/components/plans-tab/faq-item';
import { FaqSection } from 'src/pages/Settings/memberships/components/plans-tab/faq-section';
import { MemoFeatureCard } from 'src/pages/Settings/memberships/components/plans-tab/feature-card';
import { FeaturesSection } from 'src/pages/Settings/memberships/components/plans-tab/features-section';
import {
  createNewFaqQuestion,
  createNewFeature,
  createNewPlan,
  DEFAULT_PLANS_TAB_VALUES,
  NO_PLATFORM_SLA_OPTION,
} from 'src/pages/Settings/memberships/components/plans-tab/form-values';
import { SortablePlanItem } from 'src/pages/Settings/memberships/components/plans-tab/sortable-plan-item';
import {
  PlansCounterChip,
  PlansList,
  SectionAccordion,
  SectionAccordionDetails,
  SectionAccordionSummary,
  SectionSummaryContent,
  SectionSummaryTitle,
} from 'src/pages/Settings/memberships/components/plans-tab/styled';
import {
  ExpandedSection,
  PlansTabField,
  PlansTabFormValues,
  PlatformSlaFieldState,
} from 'src/pages/Settings/memberships/components/plans-tab/types';
import { useGlobalStore } from 'src/store';
import { useConfirmationModal } from 'src/utils/hooks';

const addPlanButtonSx = {
  textTransform: 'none',
  fontWeight: 500,
} as const;

export const PlansTab = ({ readOnly }: { readOnly: boolean }): React.JSX.Element => {
  const [expandedSection, setExpandedSection] = useState<ExpandedSection | null>(
    ExpandedSection.Billing,
  );
  const [expandedPlanIds, setExpandedPlanIds] = useState<string[]>([]);
  const [newlyAddedPlanId, setNewlyAddedPlanId] = useState<string | null>(null);
  const [archivedExpanded, setArchivedExpanded] = useState(false);
  const [expandedFeatureId, setExpandedFeatureId] = useState<string | null>(null);
  const [expandedFaqId, setExpandedFaqId] = useState<string | null>(null);
  const { confirm } = useConfirmationModal();
  const { control, getValues, setValue } = useFormContext<PlansTabFormValues>();
  const { checkValidity, loading: groupRulesLoading } = useGroupRules();
  const partnerId = useGlobalStore.use.selectedPartnerId();

  const {
    serviceLevelOptions,
    loading: slaOptionsLoading,
    hasServiceLevelAccess,
  } = useServiceLevels(partnerId);

  const platformSla = useMemo<PlatformSlaFieldState>(
    () => ({
      hasAccess: hasServiceLevelAccess,
      loading: slaOptionsLoading,
      options: [
        NO_PLATFORM_SLA_OPTION,
        ...serviceLevelOptions
          .filter((option) => Boolean(option.platformSlaId))
          .map((option) => ({ id: String(option.platformSlaId), label: option.label })),
      ],
    }),
    [hasServiceLevelAccess, serviceLevelOptions, slaOptionsLoading],
  );
  const canAddCustomPlan =
    !groupRulesLoading && checkValidity([UserGroupRules.AccessToAddCustomMembershipPlan]);

  const plansValues =
    (useWatch<PlansTabFormValues>({ control, name: PlansTabField.Plans }) as
      | PlansTabFormValues['plans']
      | undefined) ?? DEFAULT_PLANS_TAB_VALUES.plans;

  const { fields, move, append, remove } = useFieldArray<
    PlansTabFormValues,
    typeof PlansTabField.Plans,
    '_fieldId'
  >({
    control,
    name: PlansTabField.Plans,
    keyName: '_fieldId',
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const {
    fields: featureFields,
    append: appendFeature,
    move: moveFeature,
    remove: removeFeature,
  } = useFieldArray<PlansTabFormValues, typeof PlansTabField.Features, '_fieldId'>({
    control,
    name: PlansTabField.Features,
    keyName: '_fieldId',
  });

  const {
    fields: faqFields,
    append: appendFaq,
    move: moveFaq,
    remove: removeFaq,
  } = useFieldArray<PlansTabFormValues, typeof PlansTabField.OptionsFaqQuestions, '_fieldId'>({
    control,
    name: PlansTabField.OptionsFaqQuestions,
    keyName: '_fieldId',
  });

  useEffect(() => {
    if (expandedSection !== ExpandedSection.Plans) {
      setExpandedPlanIds([]);
    }
    if (expandedSection !== ExpandedSection.Features) {
      setExpandedFeatureId(null);
    }
    if (expandedSection !== ExpandedSection.Faq) {
      setExpandedFaqId(null);
    }
  }, [expandedSection]);

  useEffect(() => {
    setExpandedPlanIds((currentExpandedPlanIds) => {
      const enabledPlanIds = new Set(
        plansValues.filter((plan) => plan.enabled).map((plan) => plan.id),
      );
      const nextExpandedPlanIds = currentExpandedPlanIds.filter((id) =>
        enabledPlanIds.has(id),
      );

      if (nextExpandedPlanIds.length === currentExpandedPlanIds.length) {
        return currentExpandedPlanIds;
      }

      return nextExpandedPlanIds;
    });
  }, [plansValues]);

  // Plans render in the field-array order, which is the partner-defined order loaded from the
  // config (see mapActivationFlowConfigToPlansTabFormValues) and mutated by drag-and-drop below.
  const enabledPlanOptions = useMemo(() => {
    return plansValues
      .filter((plan) => plan.enabled)
      .map((plan) => ({ id: plan.id, name: plan.name }));
  }, [plansValues]);

  const planEntries = useMemo(
    () =>
      fields.map((field, index) => ({
        field,
        index,
        // An unsaved plan is always offered — its toggle is locked on — so treat it as such
        // regardless of useWatch, which reports an appended row a render later than `fields`
        // and would otherwise flash it into the Archived section.
        enabled:
          isNewPlanId(field.id) || Boolean(plansValues[index]?.enabled ?? field.enabled),
      })),
    [fields, plansValues],
  );

  const offeredEntries = useMemo(
    () => planEntries.filter((entry) => entry.enabled),
    [planEntries],
  );
  const archivedEntries = useMemo(
    () => planEntries.filter((entry) => !entry.enabled),
    [planEntries],
  );
  const offeredPlanIds = useMemo(
    () => offeredEntries.map((entry) => entry.field.id),
    [offeredEntries],
  );

  // Keeps each plan's `sortOrder` aligned with its position after a reorder, so the saved
  // `productOrder` reflects what the user sees.
  const normalizePlanSortOrder = useCallback(() => {
    const plans = getValues(PlansTabField.Plans);

    if (!Array.isArray(plans)) {
      return;
    }

    plans.forEach((plan, index) => {
      if (!plan || plan.sortOrder === index) {
        return;
      }

      setValue(`${PlansTabField.Plans}.${index}.sortOrder`, index, {
        shouldDirty: true,
        shouldTouch: true,
      });
    });
  }, [getValues, setValue]);

  const handlePlanDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (readOnly || !over || active.id === over.id) {
        return;
      }

      const from = planEntries.find((entry) => entry.field.id === String(active.id));
      const to = planEntries.find((entry) => entry.field.id === String(over.id));

      if (!from || !to) {
        return;
      }

      const firstNew = offeredEntries.find((entry) => isNewPlanId(entry.field.id));

      if (firstNew && to.index >= firstNew.index) {
        return;
      }

      move(from.index, to.index);
      normalizePlanSortOrder();
    },
    [move, normalizePlanSortOrder, offeredEntries, planEntries, readOnly],
  );

  const handleAddPlan = useCallback(() => {
    if (readOnly) {
      return;
    }

    const newPlan = createNewPlan(fields.length);
    append(newPlan);
    normalizePlanSortOrder();
    setExpandedSection(ExpandedSection.Plans);
    setExpandedPlanIds((current) => [...current, newPlan.id]);
    setNewlyAddedPlanId(newPlan.id);
  }, [append, fields.length, normalizePlanSortOrder, readOnly]);

  const handleRemovePlan = useCallback(
    async (index: number, planId: string) => {
      if (readOnly) {
        return;
      }

      const confirmed = await confirm({
        title: 'Remove plan?',
        body: "This plan hasn't been saved yet, so it's gone once you remove it.",
        confirmLabel: 'Remove plan',
        danger: true,
      });

      if (!confirmed) {
        return;
      }

      remove(index);
      normalizePlanSortOrder();
      setExpandedPlanIds((current) => current.filter((id) => id !== planId));
      setNewlyAddedPlanId((current) => (current === planId ? null : current));
    },
    [confirm, normalizePlanSortOrder, readOnly, remove],
  );

  const handleSectionExpandedChange = useCallback(
    (section: ExpandedSection) => (isExpanded: boolean) => {
      setExpandedSection(isExpanded ? section : null);
    },
    [],
  );

  const normalizeFeatureSortOrder = useCallback(() => {
    const features = getValues(PlansTabField.Features);

    if (!Array.isArray(features)) {
      return;
    }

    features.forEach((feature, index) => {
      const nextSortOrder = index + 1;

      if (!feature || feature.sortOrder === nextSortOrder) {
        return;
      }

      setValue(`${PlansTabField.Features}.${index}.sortOrder`, nextSortOrder, {
        shouldDirty: true,
        shouldTouch: true,
      });
    });
  }, [getValues, setValue]);

  const handleAddFeature = useCallback(() => {
    if (readOnly) {
      return;
    }

    const newFeature = createNewFeature(featureFields.length);
    appendFeature(newFeature);
    normalizeFeatureSortOrder();
    setExpandedSection(ExpandedSection.Features);
    setExpandedFeatureId(newFeature.id);
  }, [
    appendFeature,
    featureFields.length,
    normalizeFeatureSortOrder,
    readOnly,
    setExpandedFeatureId,
    setExpandedSection,
  ]);

  const handleMoveFeature = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (readOnly) {
        return;
      }

      moveFeature(fromIndex, toIndex);
      normalizeFeatureSortOrder();
    },
    [moveFeature, normalizeFeatureSortOrder, readOnly],
  );

  const handleRemoveFeature = useCallback(
    async (index: number, featureId: string) => {
      if (readOnly) {
        return;
      }

      const confirmed = await confirm({
        title: 'Delete feature?',
        body: 'This feature will be removed from the plan.',
        confirmLabel: 'Delete feature',
        danger: true,
      });

      if (!confirmed) {
        return;
      }

      removeFeature(index);
      normalizeFeatureSortOrder();
      if (expandedFeatureId === featureId) {
        setExpandedFeatureId(null);
      }
    },
    [
      confirm,
      expandedFeatureId,
      normalizeFeatureSortOrder,
      readOnly,
      removeFeature,
      setExpandedFeatureId,
    ],
  );

  const handleDuplicateFeature = useCallback(
    (index: number) => {
      if (readOnly) {
        return;
      }

      const features = getValues(PlansTabField.Features);

      if (!Array.isArray(features)) {
        return;
      }

      const sourceFeature = features[index];

      if (!sourceFeature) {
        return;
      }

      const newFeature = {
        ...sourceFeature,
        id: crypto.randomUUID(),
        includedPlanIds: [...(sourceFeature.includedPlanIds ?? [])],
        overrideContentByPlanId: { ...(sourceFeature.overrideContentByPlanId ?? {}) },
        overrideTooltipByPlanId: { ...(sourceFeature.overrideTooltipByPlanId ?? {}) },
        sortOrder: featureFields.length,
      };

      appendFeature(newFeature);
      normalizeFeatureSortOrder();
      setExpandedSection(ExpandedSection.Features);
      setExpandedFeatureId(newFeature.id);
    },
    [
      appendFeature,
      featureFields.length,
      getValues,
      normalizeFeatureSortOrder,
      readOnly,
      setExpandedFeatureId,
      setExpandedSection,
    ],
  );

  const normalizeFaqSortOrder = useCallback(() => {
    const faqQuestions = getValues(PlansTabField.OptionsFaqQuestions);

    if (!Array.isArray(faqQuestions)) {
      return;
    }

    faqQuestions.forEach((faqQuestion, index) => {
      if (!faqQuestion || faqQuestion.sortOrder === index) {
        return;
      }

      setValue(`${PlansTabField.OptionsFaqQuestions}.${index}.sortOrder`, index, {
        shouldDirty: true,
        shouldTouch: true,
      });
    });
  }, [getValues, setValue]);

  const handleAddFaq = useCallback(() => {
    if (readOnly) {
      return;
    }

    const newFaq = createNewFaqQuestion(faqFields.length);
    appendFaq(newFaq);
    normalizeFaqSortOrder();
    setExpandedSection(ExpandedSection.Faq);
    setExpandedFaqId(newFaq.id);
  }, [
    appendFaq,
    faqFields.length,
    normalizeFaqSortOrder,
    readOnly,
    setExpandedFaqId,
    setExpandedSection,
  ]);

  const handleMoveFaq = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (readOnly) {
        return;
      }

      moveFaq(fromIndex, toIndex);
      normalizeFaqSortOrder();
    },
    [moveFaq, normalizeFaqSortOrder, readOnly],
  );

  const handleRemoveFaq = useCallback(
    async (index: number, faqId: string) => {
      if (readOnly) {
        return;
      }

      const confirmed = await confirm({
        title: 'Delete FAQ?',
        body: 'This FAQ will be removed.',
        confirmLabel: 'Delete FAQ',
        danger: true,
      });

      if (!confirmed) {
        return;
      }

      removeFaq(index);
      normalizeFaqSortOrder();
      if (expandedFaqId === faqId) {
        setExpandedFaqId(null);
      }
    },
    [confirm, expandedFaqId, normalizeFaqSortOrder, readOnly, removeFaq, setExpandedFaqId],
  );

  const handlePlanExpandedChange = useCallback(
    (planId: string, isExpanded: boolean) => {
      setExpandedPlanIds((currentExpandedPlanIds) => {
        const isAlreadyExpanded = currentExpandedPlanIds.includes(planId);

        if (isExpanded && !isAlreadyExpanded) {
          return [...currentExpandedPlanIds, planId];
        }

        if (!isExpanded && isAlreadyExpanded) {
          return currentExpandedPlanIds.filter((id) => id !== planId);
        }

        return currentExpandedPlanIds;
      });
      if (isExpanded) {
        setExpandedSection(ExpandedSection.Plans);
      }
    },
    [setExpandedSection],
  );

  const handleFeatureExpandedChange = useCallback(
    (featureId: string, isExpanded: boolean) => {
      setExpandedFeatureId(isExpanded ? featureId : null);
      if (isExpanded) {
        setExpandedSection(ExpandedSection.Features);
        setExpandedPlanIds([]);
      }
    },
    [setExpandedFeatureId, setExpandedSection],
  );

  const handleFaqExpandedChange = useCallback(
    (faqId: string, isExpanded: boolean) => {
      setExpandedFaqId(isExpanded ? faqId : null);
      if (isExpanded) {
        setExpandedSection(ExpandedSection.Faq);
        setExpandedPlanIds([]);
        setExpandedFeatureId(null);
      }
    },
    [setExpandedFaqId, setExpandedFeatureId, setExpandedSection],
  );

  return (
    <Stack component="section" gap={1}>
      <BillingOptions
        expanded={expandedSection === ExpandedSection.Billing}
        onExpandedChange={handleSectionExpandedChange(ExpandedSection.Billing)}
        readOnly={readOnly}
      />

      <SectionAccordion
        elevation={0}
        expanded={expandedSection === ExpandedSection.Plans}
        slotProps={{ transition: { mountOnEnter: true, unmountOnExit: true } }}
        onChange={(_, expandedValue) => {
          handleSectionExpandedChange(ExpandedSection.Plans)(expandedValue);
        }}
        disableGutters
      >
        <SectionAccordionSummary
          expandIcon={<DynamicIcon name="chevron-right" library="lucide" />}
        >
          <SectionSummaryContent>
            <SectionSummaryTitle variant="h6">Plans</SectionSummaryTitle>
            <PlansCounterChip label={`${offeredEntries.length}`} size="small" />
          </SectionSummaryContent>
        </SectionAccordionSummary>

        <SectionAccordionDetails
          sx={{
            pl: 2,
          }}
        >
          {expandedSection === ExpandedSection.Plans ? (
            <Stack gap={1.5} pt={1} alignItems="center">
              {offeredEntries.length ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handlePlanDragEnd}
                >
                  <SortableContext
                    items={offeredPlanIds}
                    strategy={verticalListSortingStrategy}
                  >
                    <PlansList>
                      {offeredEntries.map(({ field, index }) => (
                        <SortablePlanItem
                          key={field._fieldId}
                          index={index}
                          planId={field.id}
                          expanded={expandedPlanIds.includes(field.id)}
                          onExpandedChange={handlePlanExpandedChange}
                          onRemove={handleRemovePlan}
                          autoFocusName={field.id === newlyAddedPlanId}
                          isNew={isNewPlanId(field.id)}
                          platformSla={platformSla}
                          readOnly={readOnly}
                          reorderDisabled={readOnly || isNewPlanId(field.id)}
                        />
                      ))}
                    </PlansList>
                  </SortableContext>
                </DndContext>
              ) : null}

              {archivedEntries.length ? (
                <ArchivedPlansSection
                  expanded={archivedExpanded}
                  onExpandedChange={setArchivedExpanded}
                  count={archivedEntries.length}
                >
                  <PlansList>
                    {archivedEntries.map(({ field, index }) => (
                      <ArchivedPlanItem
                        key={field._fieldId}
                        index={index}
                        planId={field.id}
                        onRemove={handleRemovePlan}
                        platformSla={platformSla}
                        readOnly={readOnly}
                      />
                    ))}
                  </PlansList>
                </ArchivedPlansSection>
              ) : null}
              {canAddCustomPlan && !readOnly ? (
                <Button
                  startIcon={<DynamicIcon name="plus" library="lucide" />}
                  variant="text"
                  onClick={handleAddPlan}
                  sx={addPlanButtonSx}
                >
                  Add plan
                </Button>
              ) : null}
            </Stack>
          ) : null}
        </SectionAccordionDetails>
      </SectionAccordion>

      <FeaturesSection
        expanded={expandedSection === ExpandedSection.Features}
        onExpandedChange={handleSectionExpandedChange(ExpandedSection.Features)}
        count={featureFields.length}
        onAddFeature={handleAddFeature}
        readOnly={readOnly}
      >
        <Stack gap={0}>
          {featureFields.map((featureField, index) => {
            const featureId = featureField.id;
            return (
              <MemoFeatureCard
                key={featureField._fieldId}
                index={index}
                featureId={featureId}
                enabledPlans={enabledPlanOptions}
                expanded={expandedFeatureId === featureId}
                onExpandedChange={handleFeatureExpandedChange}
                onRemove={handleRemoveFeature}
                onDuplicate={handleDuplicateFeature}
                onMove={handleMoveFeature}
                moveUpToIndex={index > 0 ? index - 1 : undefined}
                moveDownToIndex={index < featureFields.length - 1 ? index + 1 : undefined}
                readOnly={readOnly}
              />
            );
          })}
        </Stack>
      </FeaturesSection>

      <FaqSection
        expanded={expandedSection === ExpandedSection.Faq}
        onExpandedChange={handleSectionExpandedChange(ExpandedSection.Faq)}
        totalCount={faqFields.length}
        showFaqFieldName={PlansTabField.OptionsShowFaq}
        onAddFaq={handleAddFaq}
        readOnly={readOnly}
      >
        <Stack gap={0}>
          {faqFields.map((faqField, index) => {
            const faqId = faqField.id;
            return (
              <MemoFaqItem
                key={faqField._fieldId}
                index={index}
                faqId={faqId}
                expanded={expandedFaqId === faqId}
                onExpandedChange={handleFaqExpandedChange}
                onRemove={handleRemoveFaq}
                onMove={handleMoveFaq}
                moveUpToIndex={index > 0 ? index - 1 : undefined}
                moveDownToIndex={index < faqFields.length - 1 ? index + 1 : undefined}
                readOnly={readOnly}
              />
            );
          })}
        </Stack>
      </FaqSection>

      <CtaSupportSection
        expanded={expandedSection === ExpandedSection.CtaSupport}
        onExpandedChange={handleSectionExpandedChange(ExpandedSection.CtaSupport)}
        readOnly={readOnly}
      />
    </Stack>
  );
};
